import React from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'

import ClinicalProcessRow, {
	ClinicalProcessListHeader,
	ClinicalProcessRowSkeleton,
} from '../../components/cards/ClinicalProcessRow/ClinicalProcessRow'
import ClinicalProcess from '../../models/ClinicalProcess'

import themeStyles from 'assets/styles/theme.module.sass'
import { useFetchList } from 'store'

import styles from './ClinicalProcessesController.module.sass'
import NoData from 'components/general/NoData/NoData'
import ClinicalProcessesFilters from './ClinicalProcessesFilters'
import LazyLoader from 'components/general/LazyLoader/LazyLoader'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'

const { rowHeight, rowMargin } = themeStyles
const DEFAULT_PAGE_SIZE = 48

const ClinicalProcessesController = () => {
	const location = useLocation()
	const { i18n, screenSize } = useGlobal()
	const [queryParams, , setSearchParams] = useQueryParams(location)
	const pageList = new Array(parseInt(queryParams.page, 10) || 1)
	const pagesRequests = pageList.fill(null).map((_, index) => {
		const pageQueryParams = {
			...queryParams,
			page: index,
			pageSize: DEFAULT_PAGE_SIZE,
		}
		const qs = queryString.stringify(pageQueryParams)
		const pageUrl = `/clinical_processes?${qs || ''}`
		return pageUrl
	})

	const pagesMappers = pageList.fill(ClinicalProcess.fromJSON)

	const { contents: pages } = useFetchList(
		pagesRequests,
		pagesMappers,
		undefined,
		true
	)
	const isLoading =
		!pages?.length || pages.some(({ state }) => state === 'loading')

	const lastPageContents = pages[pages.length - 1]?.contents
	const hasMore =
		Array.isArray(lastPageContents) &&
		lastPageContents.length === DEFAULT_PAGE_SIZE

	const items =
		isLoading && !pages.length
			? new Array(DEFAULT_PAGE_SIZE).fill({
					$cardName: 'ClinicalProcessRowSkeleton',
					$layout: screenSize,
			  })
			: pages
					.map(({ state, contents }) => {
						const isPageLoading = state === 'loading'
						return isPageLoading
							? new Array(DEFAULT_PAGE_SIZE).fill({
									$cardName: 'ClinicalProcessRowSkeleton',
									$layout: screenSize,
							  })
							: Array.isArray(contents)
							? contents.map((data) => ({
									...data,
									$cardName: 'ClinicalProcessRow',
									$layout: screenSize,
							  }))
							: []
					})
					.flat()

	return (
		<>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				<ClinicalProcessesFilters />
			</Widget>
			<Widget
				className={styles['list-header']}
				paddings={['normal', 'normal', '']}
				applyMaxWidth
			>
				<ClinicalProcessListHeader $layout={screenSize} />
			</Widget>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				{!isLoading && !items.length ? (
					<NoData
						applyMaxWidth
						paddings={['normal', 'normal']}
						message={i18n`Current filters do not match any process.\n\nTry with different filters or reset them.`}
					/>
				) : null}
				<Collection
					className={styles['processes-list']}
					items={items}
					itemsHeight={parseFloat(rowHeight) + parseFloat(rowMargin)}
					renderAhead={20}
					cards={{
						ClinicalProcessRowSkeleton,
						ClinicalProcessRow,
					}}
				/>
			</Widget>
			<Widget paddings={['', 'normal', 'normal']} applyMaxWidth>
				<LazyLoader
					isLoading={isLoading}
					hasMore={hasMore}
					onVisible={() => {
						const nextPage = parseInt(queryParams.page, 10) || 1
						setSearchParams({ page: nextPage + 1 })
					}}
				/>
			</Widget>
		</>
	)
}

export default ClinicalProcessesController
